/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnInit, inject} from '@angular/core';
import {StateService} from '@uirouter/core';
import {UIRouterModule} from '@uirouter/angular';
import {PopoverDirective} from 'ngx-bootstrap/popover';
import {PopoverModule} from 'ngx-bootstrap/popover';
import template from './user-list-page.component.html';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../../common/controller/signal-admin-resource-list-state';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {UserApiService, UserModel} from './user-api.service';
import {WINDOW} from '../../angular-platform-providers';
import {APP_SHARED} from '../../common/app-shared';
import {ADMIN_RESOURCE_PAGES} from '../../common/controller/admin-resource-pages';

interface MasqueradeBrowsingGuide {
  browserName: string;
  contextMenuModifier: string;
  contextMenuOption: string;
  privateWindowName: string;
  shortcut: string;
  usesOptionContextMenu: boolean;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, UIRouterModule, PopoverModule, ...ADMIN_RESOURCE_PAGES],
  selector: 'adn-user-list-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserListPageComponent implements OnInit {
  readonly itemStateName = 'app.adm.users.user';
  readonly showMasqueradeLogin: boolean;
  readonly currentUser: string;

  readonly columns: SignalAdminListColumn[] = [
    {param: 'username'},
    {param: 'name'},
    {param: 'lastLogin'},
    {param: 'lastPasswordChange'},
    {param: 'createTime'},
    {param: '', label: 'role', sortable: false}
  ];

  readonly list = inject(SignalAdminResourceListStateFactory).create<UserModel>(
    inject(UserApiService),
    {listId: 'fullUserList', defaultOrderBy: 'username'},
    inject(DestroyRef)
  );

  constructor(
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly $state: StateService,
    @Inject(WINDOW) private readonly window: Window
  ) {
    this.showMasqueradeLogin = !!this.localUserProfile.get('supportUserId');
    this.currentUser = this.localUserProfile.get('username');
  }

  @Input()
  set readOnly(value: boolean) {
    this.list.readOnly.set(value);
  }

  ngOnInit(): void {
    this.list.load();
  }

  masqueradeLoginUrl(user: any): string {
    return this.$state.href('sign-in.masquerade', {
      supportUsername: this.localUserProfile.get('supportUserId'),
      masqUsername: user.username
    }, {absolute: true, inherit: false}) || '';
  }

  get masqueradeBrowsingGuide(): MasqueradeBrowsingGuide {
    const navigator = this.window.navigator;
    const userAgent = navigator.userAgent || '';
    const isMac = /Macintosh|Mac OS X/.test(userAgent) || /Mac/.test(navigator.platform || '');
    const shortcutPrefix = isMac ? '⌘ Shift' : 'Ctrl Shift';

    if (/Firefox|FxiOS/.test(userAgent)) {
      return {
        browserName: 'Firefox',
        contextMenuModifier: 'Shift',
        contextMenuOption: 'Open Link in New Private Window',
        privateWindowName: 'Private',
        shortcut: `${shortcutPrefix} P`,
        usesOptionContextMenu: false
      };
    }
    if (/Edg\//.test(userAgent)) {
      return {
        browserName: 'Edge',
        contextMenuModifier: 'Shift',
        contextMenuOption: 'Open link in InPrivate window',
        privateWindowName: 'InPrivate',
        shortcut: `${shortcutPrefix} N`,
        usesOptionContextMenu: false
      };
    }
    if (/Chrome|CriOS|Chromium/.test(userAgent)) {
      return {
        browserName: 'Chrome',
        contextMenuModifier: 'Shift',
        contextMenuOption: 'Open link in Incognito window',
        privateWindowName: 'Incognito',
        shortcut: `${shortcutPrefix} N`,
        usesOptionContextMenu: false
      };
    }
    if (isMac && /Safari\//.test(userAgent)) {
      return {
        browserName: 'Safari',
        contextMenuModifier: 'Option',
        contextMenuOption: 'Open Link in New Private Window',
        privateWindowName: 'Private',
        shortcut: `${shortcutPrefix} N`,
        usesOptionContextMenu: true
      };
    }
    return {
      browserName: 'your browser',
      contextMenuModifier: 'Shift',
      contextMenuOption: 'the private or incognito window option',
      privateWindowName: 'private',
      shortcut: `${shortcutPrefix} N`,
      usesOptionContextMenu: false
    };
  }

  showMasqueradeInstructions(event: MouseEvent, popover: PopoverDirective): void {
    event.preventDefault();
    event.stopPropagation();
    popover.show();
  }

  handleMasqueradeContextMenu(event: MouseEvent, popover: PopoverDirective): void {
    const guide = this.masqueradeBrowsingGuide;
    const shouldOpenBrowserMenu = guide.usesOptionContextMenu ? event.altKey : event.shiftKey;
    if (shouldOpenBrowserMenu) {
      popover.hide();
      return;
    }
    this.showMasqueradeInstructions(event, popover);
  }

}
